/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { LAppDelegate } from './lappdelegate';
import * as LAppDefine from './lappdefine';
import { LAppGlManager } from './lappglmanager';
import { LAppLive2DManager } from './lapplive2dmanager';
import { setGlobalResolutionTier, getGlobalResolutionTier } from './lappmodel';

type CameraPreset = {
  scale: number;
  duration: number;
  modelYOffset?: number;
  modelYOffsetDuration?: number;
  modelXOffset?: number;
  modelXOffsetDuration?: number;
};

const CAMERA_MODE_PRESETS: Record<number, CameraPreset> = {
  0: {
    scale: 0.9,
    duration: 500,
    modelYOffset: LAppDefine.ModelInitialYOffset,
    modelXOffset: 0
  },
  1: {
    scale: 1.08,
    duration: 1000,
    modelYOffset: LAppDefine.ModelZoomYOffset,
    modelXOffset: 0
  },
  2: {
    scale: 0.8,
    duration: 500,
    modelYOffset: LAppDefine.ModelZoomYOffset,
    modelXOffset: -0.15
  }
};

/**
 * ブラウザロード後の処理
 */
window.addEventListener(
  'load',
  (): void => {
    // Initialize WebGL and create the application instance
    if (
      !LAppGlManager.getInstance() ||
      !LAppDelegate.getInstance().initialize()
    ) {
      return;
    }

    LAppDelegate.getInstance().run();
    setupControlPanel();

    // Expose LAppLive2DManager for model readiness checks from Flutter
    window.LAppLive2DManager = LAppLive2DManager;
  },
  { passive: true }
);

/**
 * 終了時の処理
 */
window.addEventListener(
  'beforeunload',
  (): void => LAppDelegate.releaseInstance(),
  { passive: true }
);

/**
 * Process when changing screen size.
 */
window.addEventListener(
  'resize',
  () => {
    if (LAppDefine.CanvasSize === 'auto') {
      LAppDelegate.getInstance().onResize();
    }
  },
  { passive: true }
);
declare global {
  interface Window {
    listExpression: (i?: number) => void;
    playExpression: (expressionName: string, i?: number) => void;
    listMotions: (i?: number) => void;
    playMotion: (motionNameOrFile: string, motionIdOrModelIndex?: number, modelIndex?: number) => void;
    startTalking: (speed?: number, intensity?: number, i?: number) => void;
    endTalking: (i?: number) => void;
    isTalking: (i?: number) => boolean;
    setTalkingSpeed: (i: number, speed: number) => void;
    setTalkingIntensity: (i: number, intensity: number) => void;
    // Channel Expression System functions
    playChannelExpression: (channel: string, expressionName: string, duration: number, i?: number) => void;
    playChannelExpressionInstant: (channel: string, expressionName: string, i?: number) => void;
    playMultipleChannelExpressions: (channel: string, expressionNames: string[], isInstant: boolean, i?: number) => void;
    stopChannelExpression: (channel: string, i?: number) => void;
    getCurrentChannelExpression: (channel: string, i?: number) => string | null;
    getAllActiveChannelExpressions: (i?: number) => { [channel: string]: string };
    clearExpressionChannel: (channel: string, i?: number) => void;
    clearAllExpressionChannels: (i?: number) => void;
    getExpressionChannelDuration: (channel: string, i?: number) => number;
    // Resolution/Quality control
    setLive2DResolution: (tier: string) => Promise<boolean> | boolean;
    setLive2DResolutionTierOnly: (tier: string) => boolean;
  getLive2DResolutionTier: () => string | null;
    setPendingWardrobeExpressions: (
      snapshot: Record<string, string[] | string> | null
    ) => void;
    pauseLive2DRendering: () => void;
    resumeLive2DRendering: () => void;
  reloadCurrentModelWithResolution: () => Promise<boolean> | boolean;
    // Expose LAppLive2DManager for model readiness checks
    LAppLive2DManager: typeof LAppLive2DManager;
    // Model management
    changeModel: (modelId: string) => boolean;
    preloadModel: (modelId: string) => boolean;
    getCurrentModelId: () => string | null;
    listAvailableModels: () => string[];
    // Admin function to show control panel
    useAdmin: () => void;
    __Live2DPendingWardrobeExpressions?: Record<string, string[]> | null;
    __Live2DLoadStartTime?: number;
    setCameraMode: (modeId: number) => boolean;
  }
}

window.reloadCurrentModelWithResolution = async () => {
  const manager = LAppLive2DManager.getInstance();
  if (!manager) {
    console.error('[Live2D] LAppLive2DManager not initialized');
    return false;
  }

  return manager.reloadCurrentModelWithResolution();
};

window.setPendingWardrobeExpressions = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') {
    (window as any).__Live2DPendingWardrobeExpressions = null;
    console.log('[Live2D] Cleared pending wardrobe expressions');
    return;
  }

  const normalized: Record<string, string[]> = {};

  for (const [channel, value] of Object.entries(snapshot)) {
    let expressions: string[] = [];
    if (Array.isArray(value)) {
      expressions = value
        .map((expr) => (typeof expr === 'string' ? expr.trim() : ''))
        .filter((expr) => expr.length > 0);
    } else if (typeof value === 'string') {
      expressions = value
        .split('+')
        .map((expr) => expr.trim())
        .filter((expr) => expr.length > 0);
    }

    if (expressions.length > 0) {
      normalized[channel] = expressions;
    }
  }

  const channelCount = Object.keys(normalized).length;
  (window as any).__Live2DPendingWardrobeExpressions =
    channelCount > 0 ? normalized : null;

  if (channelCount > 0) {
    console.log(
      `[Live2D] Registered pending wardrobe expressions for ${channelCount} channel${channelCount === 1 ? '' : 's'}`
    );
  } else {
    console.log('[Live2D] No valid wardrobe expressions supplied; clearing pending cache');
  }
};

window.pauseLive2DRendering = () => {
  console.log("BB pauseLive2DRendering");
  const delegate = LAppDelegate.getInstance();
  if (delegate) {
    delegate.pauseRendering();
  }
};

window.resumeLive2DRendering = () => {
  setTimeout(() => {
    const delegate = LAppDelegate.getInstance();
    if (delegate) {
      delegate.resumeRendering();
    }
  }, 10);
};

window.setCameraMode = (modeId: number): boolean => {
  const preset = CAMERA_MODE_PRESETS[modeId] ?? CAMERA_MODE_PRESETS[0];
  const delegate = LAppDelegate.getInstance();

  if (!delegate) {
    console.error('[Live2D] LAppDelegate not initialized, cannot set camera');
    return false;
  }

  const view = delegate.getView();
  if (!view || typeof view.setCameraPreset !== 'function') {
    console.error('[Live2D] LAppView not ready, cannot set camera');
    return false;
  }

  view.setCameraPreset(preset.scale, preset.duration);
  const manager = LAppLive2DManager.getInstance();
  if (manager && typeof manager.setModelYOffset === 'function') {
    const targetOffset =
      preset.modelYOffset ?? LAppDefine.ModelInitialYOffset ?? 0;
    const offsetDuration =
      preset.modelYOffsetDuration ?? preset.duration ?? 0;
    manager.setModelYOffset(targetOffset, offsetDuration);
  }
  if (manager && typeof manager.setModelXOffset === 'function') {
    const targetXOffset = preset.modelXOffset ?? 0;
    const xOffsetDuration = preset.modelXOffsetDuration ?? preset.duration ?? 0;
    manager.setModelXOffset(targetXOffset, xOffsetDuration);
  }
  return true;
};
window.listExpression = (i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null || !model._modelSetting) {
    console.error('Model not loaded yet - cannot list expressions');
    return;
  }
  console.log(model._expressions);
}
window.playExpression = (expressionName: string, i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null || !model._modelSetting) {
    console.error('Model not loaded yet - cannot play expression');
    return;
  }
  model.setExpression(expressionName);
}
window.listMotions = (i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null || !model._modelSetting) {
    console.error('Model not loaded yet - cannot list motions');
    return;
  }
  console.log(model._motions);
}

/**
 * Start a motion with flexible parameters:
 * - playMotion(fileName, modelIndex?) - e.g., playMotion("Idle_00", 0) or playMotion("Idle_00.motion3.json")
 * - playMotion(motionName, motionId, modelIndex?) - e.g., playMotion("Idle", 0, 0)
 */
window.playMotion = (motionNameOrFile: string, motionIdOrModelIndex?: number, modelIndex?: number) => {
  let motionName: string;
  let motionId: number;
  let targetModelIndex: number;

  if (modelIndex !== undefined) {
    // Old way: playMotion(motionName, motionId, modelIndex)
    motionName = motionNameOrFile;
    motionId = motionIdOrModelIndex!;
    targetModelIndex = modelIndex;
  } else if (motionIdOrModelIndex !== undefined && typeof motionIdOrModelIndex === 'number') {
    // Ambiguous case: could be either way
    // First, try as filename with modelIndex (new way)
    targetModelIndex = motionIdOrModelIndex;

    let model = LAppLive2DManager.getInstance().getModel(targetModelIndex);
    if (model != null && model._modelSetting) {
      const motionInfo = model.findMotionByFileName(motionNameOrFile);
      if (motionInfo) {
        // Found as filename: playMotion(fileName, modelIndex)
        motionName = motionInfo.group;
        motionId = motionInfo.index;
      } else {
        // Not found as filename at this index, try old way with modelIndex=0
        targetModelIndex = 0;
        model = LAppLive2DManager.getInstance().getModel(targetModelIndex);
        if (model == null || !model._modelSetting) {
          console.error('Model not loaded yet - cannot play motion');
          return;
        }
        motionName = motionNameOrFile;
        motionId = motionIdOrModelIndex;
      }
    } else {
      // Model at that index doesn't exist, assume old way: playMotion(motionName, motionId) with modelIndex=0
      targetModelIndex = 0;
      model = LAppLive2DManager.getInstance().getModel(targetModelIndex);
      if (model == null || !model._modelSetting) {
        console.error('Model not loaded yet - cannot play motion');
        return;
      }
      motionName = motionNameOrFile;
      motionId = motionIdOrModelIndex;
    }
  } else {
    // New way: playMotion(fileName) with modelIndex=0
    targetModelIndex = 0;

    const model = LAppLive2DManager.getInstance().getModel(targetModelIndex);
    if (model == null || !model._modelSetting) {
      console.error('Model not loaded yet - cannot play motion');
      return;
    }

    const motionInfo = model.findMotionByFileName(motionNameOrFile);
    if (motionInfo) {
      motionName = motionInfo.group;
      motionId = motionInfo.index;
    } else {
      console.error(`Motion file not found: ${motionNameOrFile}`);
      return;
    }
  }

  const model = LAppLive2DManager.getInstance().getModel(targetModelIndex);
  if (model == null || !model._modelSetting) {
    console.error('Model not loaded yet - cannot play motion');
    return;
  }

  model.playMotion(motionName, motionId, 100);
}

// Talking animation global functions
window.startTalking = (speed: number = 4.0, intensity: number = 0.6, i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return;
  }
  model.startTalking(speed, intensity);
}

window.endTalking = (i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return;
  }
  model.endTalking();
}

window.isTalking = (i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return false;
  }
  return model.isTalking();
}

window.setTalkingSpeed = (i: number, speed: number) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return;
  }
  model.setTalkingSpeed(speed);
}

window.setTalkingIntensity = (i: number, intensity: number) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return;
  }
  model.setTalkingIntensity(intensity);
}

// Channel Expression System global functions
window.playChannelExpression = (channel: string, expressionName: string, duration: number, i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return;
  }
  model.playExpression(channel, expressionName, duration);
}

// Instant version that skips fade animation (for initialization)
window.playChannelExpressionInstant = (channel: string, expressionName: string, i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return;
  }
  model.playExpressionInstant(channel, expressionName);
}

// Apply multiple expressions to a single channel (layered)
window.playMultipleChannelExpressions = (channel: string, expressionNames: string[], isInstant: boolean, i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return;
  }
  model.playMultipleExpressions(channel, expressionNames, isInstant);
}

window.stopChannelExpression = (channel: string, i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return;
  }
  model.stopExpression(channel);
}

window.getCurrentChannelExpression = (channel: string, i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return null;
  }
  return model.getCurrentExpression(channel);
}

window.getAllActiveChannelExpressions = (i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return {};
  }
  return model.getAllActiveExpressions();
}

window.clearExpressionChannel = (channel: string, i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return;
  }
  model.clearChannel(channel);
}

window.clearAllExpressionChannels = (i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return;
  }
  model.clearAllExpressions();
}

window.getExpressionChannelDuration = (channel: string, i: number = 0) => {
  const model = LAppLive2DManager.getInstance().getModel(i);
  if (model == null) {
    console.error('Model not loaded yet');
    return 0;
  }
  return model.getChannelDuration(channel);
}

// Resolution/Quality control function
window.setLive2DResolution = async (tier: string) => {
  try {
    console.log(`[Live2D] Setting resolution tier to: ${tier}`);

    // Validate tier
    if (!['low', 'medium', 'high'].includes(tier)) {
      console.warn(`[Live2D] Unknown resolution tier: ${tier}, defaulting to high`);
      tier = 'high';
    }

    // Set the global resolution preference
    setGlobalResolutionTier(tier);

    // Get the current model to reload it with new textures
    const manager = LAppLive2DManager.getInstance();
    if (!manager) {
      console.error('[Live2D] LAppLive2DManager not initialized');
      return false;
    }

    const currentModelId = window.getCurrentModelId();
    if (!currentModelId) {
      console.warn('[Live2D] No model currently loaded, resolution will apply on next model load');
      return true;
    }

    console.log(`[Live2D] Reloading model "${currentModelId}" with ${tier} resolution textures...`);

    // Use smooth transition to keep old model visible while loading new one
    const success = await manager.reloadCurrentModelWithResolution();

    if (success) {
      console.log(`[Live2D] ✓ Resolution changed to ${tier} successfully (smooth transition)`);
    } else {
      console.error(`[Live2D] ✗ Failed to reload model with new resolution`);
    }

    return success;
  } catch (error) {
    console.error('[Live2D] Failed to set resolution:', error);
    return false;
  }
}

window.setLive2DResolutionTierOnly = (tier: string) => {
  try {
    if (!['low', 'medium', 'high'].includes(tier)) {
      console.warn(`[Live2D] Unknown resolution tier: ${tier}, ignoring request.`);
      return false;
    }

    console.log(`[Live2D] Updating global resolution tier to ${tier} without reload`);
    setGlobalResolutionTier(tier);
    return true;
  } catch (error) {
    console.error('[Live2D] Failed to set resolution tier (no reload):', error);
    return false;
  }
};

window.getLive2DResolutionTier = () => {
  try {
    return getGlobalResolutionTier();
  } catch (error) {
    console.error('[Live2D] Failed to get resolution tier:', error);
    return null;
  }
};

// Model management functions
window.changeModel = (modelId: string) => {
  // Track load start time for timing measurements
  (window as any).__Live2DLoadStartTime = performance.now();

  const manager = LAppLive2DManager.getInstance();
  if (manager == null) {
    console.error('LAppLive2DManager not initialized');
    return false;
  }
  return manager.changeModel(modelId);
}


window.getCurrentModelId = () => {
  const manager = LAppLive2DManager.getInstance();
  if (manager == null) {
    console.error('LAppLive2DManager not initialized');
    return null;
  }
  const index = manager['_sceneIndex'];
  return LAppDefine.ModelDir[index] || null;
}

window.listAvailableModels = () => {
  return LAppDefine.ModelDir;
}

// Admin function to enable the control panel
window.useAdmin = () => {
  const panel = document.getElementById('control-panel');
  if (panel) {
    panel.classList.add('admin-mode');
    console.log('✅ Admin mode enabled - Control panel is now visible');
    console.log('Available commands: listExpression(), playExpression(name), listMotions(), playMotion(group, id)');
  } else {
    console.error('Control panel not found');
  }
}

function setupControlPanel(): void {
  const panel = document.getElementById('control-panel');
  const expressionContainer = document.getElementById('expression-buttons');
  const motionContainer = document.getElementById('motion-buttons');
  const expressionEmpty = document.getElementById('expression-empty');
  const motionEmpty = document.getElementById('motion-empty');

  if (!panel || !expressionContainer || !motionContainer) {
    return;
  }

  let lastSignature = '';

  const formatLabel = (name: string): string => {
    if (!name) {
      return 'Unknown';
    }
    const lastSegment = name.split(/[\\/]/).pop() ?? name;
    const withoutExtension = lastSegment
      .replace(/\.motion3\.json$/i, '')
      .replace(/\.exp3\.json$/i, '')
      .replace(/\.moc3$/i, '')
      .replace(/\.json$/i, '');
    return withoutExtension.replace(/[_-]+/g, ' ');
  };

  const refreshControls = () => {
    const manager = LAppLive2DManager.getInstance();
    if (!manager) {
      return;
    }

    const model = manager.getModel(0);
    if (!model || !model._modelSetting) {
      return;
    }

    const expressionCount = typeof model._modelSetting.getExpressionCount === 'function'
      ? model._modelSetting.getExpressionCount()
      : 0;

    const expressions: string[] = [];
    for (let i = 0; i < expressionCount; i++) {
      expressions.push(model._modelSetting.getExpressionName(i));
    }

    const motionGroups: { name: string; motions: { index: number; file: string }[] }[] = [];
    const motionGroupCount = typeof model._modelSetting.getMotionGroupCount === 'function'
      ? model._modelSetting.getMotionGroupCount()
      : 0;

    for (let gi = 0; gi < motionGroupCount; gi++) {
      const groupName = model._modelSetting.getMotionGroupName(gi);
      const motionCount = model._modelSetting.getMotionCount(groupName);
      const entries: { index: number; file: string }[] = [];

      for (let mi = 0; mi < motionCount; mi++) {
        const fileName = model._modelSetting.getMotionFileName(groupName, mi);
        entries.push({ index: mi, file: fileName });
      }

      motionGroups.push({ name: groupName, motions: entries });
    }

    const signature = JSON.stringify({
      expressions,
      motions: motionGroups.map((group) => ({
        name: group.name,
        files: group.motions.map((motion) => motion.file),
      })),
    });

    if (signature === lastSignature) {
      return;
    }

    lastSignature = signature;

    expressionContainer.innerHTML = '';
    if (expressions.length === 0) {
      if (expressionEmpty) {
        expressionEmpty.removeAttribute('hidden');
      }
    } else {
      if (expressionEmpty) {
        expressionEmpty.setAttribute('hidden', '');
      }

      expressions.forEach((expressionName) => {
        const button = document.createElement('button');
        button.className = 'control-button';
        button.textContent = formatLabel(expressionName);
        button.title = expressionName;
        button.addEventListener('click', () => {
          // Check if model is still loaded before playing
          const currentModel = LAppLive2DManager.getInstance().getModel(0);
          if (!currentModel || !currentModel._modelSetting) {
            console.error('Model not loaded yet - cannot play expression');
            return;
          }
          window.playExpression(expressionName);
        });
        expressionContainer.appendChild(button);
      });
    }

    motionContainer.innerHTML = '';
    if (motionGroups.length === 0) {
      if (motionEmpty) {
        motionEmpty.removeAttribute('hidden');
      }
      return;
    }

    if (motionEmpty) {
      motionEmpty.setAttribute('hidden', '');
    }

    motionGroups.forEach((group) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'motion-group';

      const heading = document.createElement('div');
      heading.className = 'group-title';
      heading.textContent = group.name;
      wrapper.appendChild(heading);

      const grid = document.createElement('div');
      grid.className = 'button-grid';

      group.motions.forEach(({ index, file }) => {
        const button = document.createElement('button');
        button.className = 'control-button';
        const labelSource = file || `${group.name}_${index}`;
        const label = formatLabel(labelSource) || `${group.name} ${index + 1}`;
        button.textContent = label;
        button.title = labelSource;
        button.addEventListener('click', () => {
          // Check if model is still loaded before playing
          const currentModel = LAppLive2DManager.getInstance().getModel(0);
          if (!currentModel || !currentModel._modelSetting) {
            console.error('Model not loaded yet - cannot play motion');
            return;
          }
          window.playMotion(group.name, index);
        });
        grid.appendChild(button);
      });

      wrapper.appendChild(grid);
      motionContainer.appendChild(wrapper);
    });
  };

  const REFRESH_INTERVAL_MS = 800;
  refreshControls();
  window.setInterval(refreshControls, REFRESH_INTERVAL_MS);
}
